/**
 * Loads active episode-of-care service types for intake Step 1 via the catalog bot.
 */

import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import {
  getActiveIntakeServices,
  type IntakeServiceCatalogEntry,
} from './intakeServicesCatalog';

export interface UseActiveIntakeServicesReturn {
  services: IntakeServiceCatalogEntry[];
  loading: boolean;
  error: string | null;
}

export function useActiveIntakeServices(enabled: boolean): UseActiveIntakeServicesReturn {
  const medplum = useMedplum();
  const [services, setServices] = useState<IntakeServiceCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void getActiveIntakeServices(medplum)
      .then((result) => {
        if (cancelled) return;
        setServices(result.services ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setServices([]);
        setError(err instanceof Error ? err.message : 'Failed to load services');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, medplum]);

  return { services, loading: enabled ? loading : true, error };
}
